import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Grid, Paper, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { ComparacaoConsultores } from '@/components/dashboard/ComparacaoConsultores'
import { FunilPorCanal } from '@/components/dashboard/FunilPorCanal'
import { DistribuicaoPorCanal } from '@/components/dashboard/DistribuicaoPorCanal'
import { GraficoColunas } from '@/components/dashboard/GraficoColunas'
import { CardMetas } from '@/components/dashboard/CardMetas'
import { TituloSecao } from '@/components/dashboard/TituloSecao'
import {
  CANAIS_DE_RECORTE,
  CANAL_RECORTE_LABEL,
  CanalDeRecorte,
  formatBRL,
  formatBRLCurto,
} from '@/components/dashboard/canais'
import { resumoAdminService } from '@/services/resumoAdminService'
import { DashboardResumoAdmin } from '@/types/resumoAdmin'
import { authStore } from '@/store/authStore'
import { canalStore } from '@/store/canalStore'
import { ETAPAS_COMERCIAIS, EtapaComercial } from '@/types/statusReserva'
import { tokens } from '@/theme'

/**
 * "Em carteira" = as etapas comerciais do funil exceto PERDIDO — a MESMA
 * partição (`pipeline.porEtapaComercial` / `valorPorEtapaComercial`) que o
 * Funil por canal desenha logo abaixo. Nenhuma métrica nova: só a soma das
 * fatias que o backend devolveu. Lacuna para o A03: `reservasAtivas` e
 * `volumeEmCarteira` prontos no resumo, para nem essa soma viver na tela.
 */
const ETAPAS_EM_CARTEIRA = ETAPAS_COMERCIAIS.filter((e) => e !== 'PERDIDO')

const reservasAtivas = (porEtapa: Record<string, number> | undefined): number =>
  ETAPAS_EM_CARTEIRA.reduce((acc, e) => acc + (porEtapa?.[e] ?? 0), 0)

const volumeEmCarteira = (valorPorEtapa: Record<string, number> | undefined): number =>
  ETAPAS_EM_CARTEIRA.reduce((acc, e) => acc + Number(valorPorEtapa?.[e] ?? 0), 0)

const totalReservas = (porEtapa: Record<string, number> | undefined): number =>
  Object.values(porEtapa ?? {}).reduce((acc, n) => acc + n, 0)

const RAMPA = ['#F9B79A', tokens.brandPrimaryLight, tokens.brandPrimary, tokens.brandPrimaryDark]

interface RecorteCanal {
  canal: CanalDeRecorte
  resumo: DashboardResumoAdmin
}

/**
 * Destino do clique em cada card do funil. Reserva abre a tela de reservas do
 * backoffice; Negociação, Ganho e Perdido não têm tela própria e caem no
 * Pipeline Comercial, que mostra as colunas por etapa.
 */
const ROTA_POR_ETAPA: Record<EtapaComercial, string> = {
  RESERVA: '/reservas-curadoria',
  REUNIAO: '/agenda',
  ESTRUTURACAO: '/estruturacao',
  NEGOCIACAO: '/pipeline',
  GANHO: '/pipeline',
  PERDIDO: '/pipeline',
}

/**
 * Visão de Carteira (Figma 6:2) — o dashboard do administrador (Onda 2 / A05;
 * conceito BI e identidade em 10–11/09).
 *
 * Tudo vem do `/v1/dashboard/resumo` (estendido pelo A03): KPIs, comparação, financeiro e
 * meta do recorte atual numa chamada; funil e distribuição por canal com uma chamada por
 * canal (`?canal=`), porque o resumo não devolve a matriz etapa × canal em lote
 * (lacuna relatada). Reage ao `SeletorCanalGlobal` (canalStore, A02): com um
 * canal selecionado, os gráficos mostram só aquele recorte.
 *
 * Montada em `/dashboard` pelo despachante por papel do `DashboardPage` (ADMIN/GERENTE);
 * a rota provisória `/visao-carteira` saiu na integração da Rodada 2.
 */
export const VisaoCarteiraAdmin: FC = () => {
  const navigate = useNavigate()
  const usuario = authStore((s) => s.usuario)
  const podeVer = !!usuario?.permissoes?.some((p) =>
    ['ADMIN', 'GERENTE'].includes(p.toUpperCase()),
  )
  const canalGlobal = canalStore((s) => s.canal)

  const [resumo, setResumo] = useState<DashboardResumoAdmin | null>(null)
  const [recortes, setRecortes] = useState<RecorteCanal[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  // Guarda de requisição mais recente (R01 item 3): duas trocas rápidas de canal
  // disparam duas cargas; se a primeira resolver depois, é descartada — a tela
  // mostra sempre o último canal escolhido. Também silencia setState pós-desmontagem.
  const requisicaoRef = useRef(0)
  useEffect(() => {
    return () => {
      requisicaoRef.current = -1
    }
  }, [])

  const carregar = useCallback(async () => {
    if (!podeVer) return
    const token = ++requisicaoRef.current
    const atual = () => requisicaoRef.current === token
    setCarregando(true)
    setErro(null)
    try {
      if (canalGlobal) {
        // Recorte único: o seletor global já escolheu o canal.
        const r = await resumoAdminService.getResumo(canalGlobal)
        if (!atual()) return
        setResumo(r)
        setRecortes([{ canal: canalGlobal as CanalDeRecorte, resumo: r }])
      } else {
        // "Todos os canais": o geral alimenta KPIs/comparação; um recorte por
        // canal (+ SEM_CANAL) alimenta funil e distribuição.
        const [geral, ...porCanal] = await Promise.all([
          resumoAdminService.getResumo(),
          ...CANAIS_DE_RECORTE.map((c) => resumoAdminService.getResumo(c)),
        ])
        if (!atual()) return
        setResumo(geral)
        setRecortes(CANAIS_DE_RECORTE.map((c, i) => ({ canal: c, resumo: porCanal[i] })))
      }
      setCarregando(false)
    } catch (e) {
      if (!atual()) return
      setErro(e instanceof Error ? e.message : 'Falha ao carregar o resumo da carteira')
      setResumo(null)
      setRecortes([])
      setCarregando(false)
    }
  }, [podeVer, canalGlobal])

  useEffect(() => {
    carregar()
  }, [carregar])

  const cabecalho = (
    <PageHeader
      title="Visão de Carteira"
      eyebrow="Principal"
      subtitle={
        canalGlobal
          ? `Carteira do canal ${CANAL_RECORTE_LABEL[canalGlobal as CanalDeRecorte] ?? canalGlobal}`
          : 'Carteira consolidada por canal e por consultor'
      }
    />
  )

  if (usuario && !podeVer) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista
          estado="semPermissao"
          detalhe="A Visão de Carteira é do back-office (ADMIN/GERENTE)."
        />
      </Box>
    )
  }

  if (erro) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista
          estado="erro"
          detalhe={erro}
          acao={{ label: 'Tentar novamente', onClick: carregar }}
        />
      </Box>
    )
  }

  const porEtapa = resumo?.pipeline?.porEtapaComercial
  const vazio = !carregando && resumo != null && totalReservas(porEtapa) === 0
  const porConsultor = resumo?.porConsultor
  const financeiro = resumo?.financeiro
  // Lacuna para o A03 (R01 item 5): o backend NÃO recorta por canal as comissões
  // (DashboardService:168) nem `porConsultor[].pendencias` (:241). Com canal
  // selecionado, mostrar o número global seria mentir sobre o recorte → "—".
  const recortadoPorCanal = !!canalGlobal

  return (
    <Box>
      {cabecalho}

      {/* KPIs — todos do /v1/dashboard/resumo, a mesma fonte dos gráficos abaixo. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="escura"
            label="Volume em carteira"
            carregando={carregando}
            valor={formatBRL(volumeEmCarteira(resumo?.pipeline?.valorPorEtapaComercial))}
            legenda="Reservas em andamento, sem as perdidas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Reservas ativas"
            carregando={carregando}
            valor={reservasAtivas(porEtapa)}
            legenda="Em andamento, sem as perdidas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Conversão"
            carregando={carregando}
            valor={resumo?.conversao != null ? `${String(resumo.conversao).replace('.', ',')}%` : '—'}
            legenda="Reservas ganhas sobre o total"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="marca"
            label="Comissões apuradas"
            carregando={carregando}
            valor={
              recortadoPorCanal
                ? '—'
                : formatBRL(Number(resumo?.financeiro?.comissaoDisponivelParaNota ?? 0))
            }
            legenda={
              recortadoPorCanal
                ? 'Não recortado por canal'
                : 'Confirmadas, ainda sem nota fiscal'
            }
          />
        </Grid>
      </Grid>

      {carregando && <EstadoDeLista estado="carregando" />}

      {vazio && (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma reserva na carteira"
          detalhe={
            canalGlobal
              ? 'Este canal ainda não tem reservas carimbadas.'
              : 'Quando as reservas chegarem, o funil e a distribuição aparecem aqui.'
          }
        />
      )}

      {!carregando && !vazio && resumo && (
        <>
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <TituloSecao
              titulo="Funil por canal"
              descricao="Quantas reservas há em cada etapa; as colunas separam por canal."
            />
            <FunilPorCanal
              series={recortes.map((r) => ({
                canal: r.canal,
                porEtapa: r.resumo.pipeline.porEtapaComercial ?? {},
                valorPorEtapa: r.resumo.pipeline.valorPorEtapaComercial ?? {},
              }))}
              rotaPorEtapa={ROTA_POR_ETAPA}
            />
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Distribuição por canal"
                  descricao="Participação de cada canal no volume em carteira."
                />
                <DistribuicaoPorCanal
                  fatias={recortes.map((r) => ({
                    canal: r.canal,
                    volume: volumeEmCarteira(r.resumo.pipeline.valorPorEtapaComercial),
                  }))}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <TituloSecao
                  titulo="Financeiro"
                  descricao={
                    recortadoPorCanal
                      ? 'Volumes do canal; comissões não são recortadas por canal.'
                      : 'Volumes e comissões — mesma fonte do Financeiro.'
                  }
                />
                <GraficoColunas
                  altura={130}
                  formatoValor={formatBRLCurto}
                  itens={[
                    {
                      chave: 'negociacao',
                      rotulo: 'Em negociação',
                      valor: Number(financeiro?.volumeEmNegociacao ?? 0),
                      cor: RAMPA[1],
                    },
                    {
                      chave: 'fechado',
                      rotulo: 'Volume fechado',
                      valor: Number(financeiro?.volumeFechado ?? 0),
                      cor: tokens.brandInk,
                    },
                    {
                      chave: 'projetada',
                      rotulo: 'Comissão projetada',
                      valor: recortadoPorCanal ? 0 : Number(financeiro?.comissaoProjetada ?? 0),
                      cor: RAMPA[2],
                    },
                    {
                      chave: 'nota',
                      rotulo: 'Disponível p/ nota',
                      valor: recortadoPorCanal
                        ? 0
                        : Number(financeiro?.comissaoDisponivelParaNota ?? 0),
                      cor: RAMPA[3],
                    },
                  ]}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                {/* fonte: GET /v1/dashboard/resumo (metas, visão global §2.4; sem `metas` → meta legado) */}
                <CardMetas
                  modo="admin"
                  metas={resumo.metas}
                  metaLegada={resumo.meta}
                  recortadoPorCanal={recortadoPorCanal}
                  onVerCampanhas={() => navigate('/campanhas')}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Só renderiza se o backend mandou o bloco (ADMIN/GERENTE — regra do A03). */}
          {porConsultor && (
            <Paper sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 700, color: tokens.brandInk, mb: 0.5 }}>
                Comparação entre consultores
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.textSecondary, display: 'block', mb: 2 }}>
                Nome registrado nas reservas do consultor (sem nome registrado, aparece o
                identificador). Canal em branco = reservas de mais de um canal.
              </Typography>
              <ComparacaoConsultores
                linhas={porConsultor}
                recortadoPorCanal={recortadoPorCanal}
                onAbrir={(c) => navigate(`/reservas-curadoria?consultorId=${c.consultorId}`)}
              />
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}

export default VisaoCarteiraAdmin
